import { Injector } from "@angular/core";
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Utilities } from "src/app/services/utilities";
import {
    IInterviewHandler,
    IVariable
} from "../../models/interview-handler.models";
import { ICreateControlOptions } from "../../template/grid-template/grid-template.models";
import { TemplateService } from "../template.service";


//#region Variable Control Creation

export interface IVariableControl {
  formControl: AbstractControl;
  handler: IInterviewHandler;
  subscriptions: Subscription[];
  variable: IVariable;
  name: string;
  injector: Injector;
  showUI: boolean;
  form: UntypedFormGroup;

  createReactiveControl(
    forceListeners?: boolean,
    options?: ICreateControlOptions
  );

  buildInitialFormControlValue();
  getVariableValue();
  clearVariableValue();

  setOnChangeEvent(
    formControl: AbstractControl,
    templateService: TemplateService
  ): void;
  getDisplayValueForGrid(item: any): any;
  getValue(item: any): any;
  getDisplayValue(item: any): any;
  dispose(): void;
}

export abstract class BaseVariableControl implements IVariableControl {
  handler: IInterviewHandler;
  name: string;
  variable: IVariable;

  get formControl(): AbstractControl {
    return this.form.get(this.variable.name);
  }
  subscriptions: Subscription[] = [];
  injector: Injector;
  showUI: boolean;

  get fmBuilder() {
    return this.handler.fmBuilder;
  }

  private _form: UntypedFormGroup;
  set form(value: UntypedFormGroup) {
    this._form = value;
  }
  get form() {
    return this._form || this.handler.form;
  }

  get zIndex() {
    return this.handler.settings?.style?.zIndex || 0;
  }

  setOnChangeEvent(formControl: AbstractControl): void {
    const subscription = formControl.valueChanges
      .pipe(debounceTime(750))
      .subscribe((value) => {
        this.variable.value = this.getVariableValue();
        this.handler.onValueChanged.next(this);
      });

    this.subscriptions.push(subscription);
  }

  getVariableValue() {
    const keyField =
      this.variable.displayMetadata &&
      this.variable.displayMetadata.templateData &&
      this.variable.displayMetadata.templateData.keyField;
    if (keyField && this.formControl.value) {
      const keyFieldCamelCase = Utilities.getVariableCaseInsensitive(
        this.formControl.value,
        keyField
      );
      return this.formControl.value[keyFieldCamelCase];
    } else {
      return this.formControl.value;
    }
  }

  clearVariableValue() {
    this.variable.value = null;
    this.formControl.reset();
  }

  setInitialFormControlValue() {
    return;
  }

  createReactiveControl(forceListeners = true) {
    this.addFormControl();

    this.setValidators(this.form.controls[this.variable.name]);

    if (
      (forceListeners && this.variable.hasDependencies) ||
      this.variable.forcePostback
    ) {
      this.variable.value = this.getVariableValue();
      this.setOnChangeEvent(this.form.controls[this.variable.name]);
    }
  }

  buildInitialFormControlValue() {
    return this.variable.value;
  }

  protected setValidators(formControl: AbstractControl) {
    return;
  }

  getDisplayValueForGrid(item: any): any {
    return (
      (item &&
        item[
          Object.getOwnPropertyNames(item).find(
            (itemName) =>
              Utilities.camelize(itemName) ===
              Utilities.camelize(this.variable.name)
          )
        ]) ||
      this.variable.value
    );
  }

  getValue(item: any): any {
    if (item) {
      return item[
        Utilities.getVariableCaseInsensitive(item, this.variable.name)
      ];
    } else {
      return this.formControl.value;
    }
  }

  getDisplayValue(item: any): any {
    if (item) {
      return item[
        Utilities.getVariableCaseInsensitive(item, this.variable.name)
      ];
    } else {
      return this.variable.value;
    }
  }

  dispose(): void {
    // clean variable's handler subscriptions
    for (let i = 0; i < this.subscriptions.length; i++) {
      try {
        this.subscriptions[i].unsubscribe();
      } catch (error) {
        console.warn(
          `Error closing subscription for ${this.variable.name}`
        );
      }

      this.subscriptions[i] = null;
    }

      if (this.formControl) {
        this.formControl.clearValidators();
        this.formControl.clearAsyncValidators();
      }
  }

  addFormControl() {

    const controlValue = this.buildInitialFormControlValue();
    const formControl = this.fmBuilder.control(controlValue);

    if(this.form.controls[this.variable.name]) {
        this.form.setControl(this.variable.name, formControl);
    } else {
        this.form.addControl(this.variable.name, formControl);
    }

  }

}
